import {
  BarVppApplicationConfiguration,
  BrandVppApplicationConfiguration,
  FrontDeskVppApplicationConfiguration,
  HousekeepingVppApplicationConfiguration,
  KioskVppApplicationConfiguration,
  LocationVppApplicationConfiguration,
  RoomVppApplicationConfiguration,
  StaffVppApplicationConfiguration,
} from '@era/settings-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface VppApplicationConfiguration {
  type: CategoryType;
  categoryId: string;
  adamIdStr: string;
  applyFrom?: Date;
  version?: string;
}

type AnyVppApplicationConfiguration =
  | BrandVppApplicationConfiguration
  | LocationVppApplicationConfiguration
  | RoomVppApplicationConfiguration
  | StaffVppApplicationConfiguration
  | KioskVppApplicationConfiguration
  | HousekeepingVppApplicationConfiguration
  | BarVppApplicationConfiguration
  | FrontDeskVppApplicationConfiguration;

/**
 * Generic function to convert any VPP application configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function vppApplicationConfigurationFrom(
  configuration: AnyVppApplicationConfiguration
): VppApplicationConfiguration {
  // Type guard functions to determine the category type
  if ('brandId' in configuration) {
    return {
      type: 'Brand',
      categoryId: configuration.brandId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('locationId' in configuration) {
    return {
      type: 'Location',
      categoryId: configuration.locationId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('roomId' in configuration) {
    return {
      type: 'Room',
      categoryId: configuration.roomId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('barId' in configuration) {
    return {
      type: 'Bar',
      categoryId: configuration.barId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('frontDeskId' in configuration) {
    return {
      type: 'FrontDesk',
      categoryId: configuration.frontDeskId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('housekeepingId' in configuration) {
    return {
      type: 'Housekeeping',
      categoryId: configuration.housekeepingId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('kioskId' in configuration) {
    return {
      type: 'Kiosk',
      categoryId: configuration.kioskId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  if ('staffId' in configuration) {
    return {
      type: 'Staff',
      categoryId: configuration.staffId,
      adamIdStr: configuration.adamIdStr,
      applyFrom: configuration.applyFrom,
      version: configuration.version,
    };
  }
  throw new Error('Unknown VPP application configuration type');
}

export type CategoryVppApplicationsState = EntityState<VppApplicationConfiguration>;

export function selectId(application: VppApplicationConfiguration) {
  return `${application.type}-${application.categoryId}-${application.adamIdStr}`;
}

export const adapter: EntityAdapter<VppApplicationConfiguration> = createEntityAdapter<VppApplicationConfiguration>({
  selectId,
});

export const initialState: CategoryVppApplicationsState = adapter.getInitialState();
